import React, { useState } from 'react';
import { FunnelElement } from '../../types/element.types';
import { useFunnelStore } from '../../store/funnelStore';

interface ResponsiveTabProps {
  element: FunnelElement;
}

/**
 * Responsive Tab Component
 * Allows setting responsive style overrides for tablet and mobile
 */
const ResponsiveTab: React.FC<ResponsiveTabProps> = ({ element }) => {
  const { updateElementResponsiveStyles } = useFunnelStore();
  const [activeBreakpoint, setActiveBreakpoint] = useState<'tablet' | 'mobile'>('tablet');

  const currentStyles = element.responsive?.[activeBreakpoint] || {};

  const handleStyleChange = (key: string, value: string) => {
    updateElementResponsiveStyles(element.id, activeBreakpoint, { [key]: value });
  };

  return (
    <div className="space-y-4">
      {/* Breakpoint Selector */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Breakpoint</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveBreakpoint('tablet')}
            className={`
              flex-1 px-4 py-2 text-sm font-medium rounded transition-all
              ${
                activeBreakpoint === 'tablet'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            Tablet (768px)
          </button>
          <button
            onClick={() => setActiveBreakpoint('mobile')}
            className={`
              flex-1 px-4 py-2 text-sm font-medium rounded transition-all
              ${
                activeBreakpoint === 'mobile'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            Mobile (375px)
          </button>
        </div>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-xs text-blue-700">
          💡 Override styles for {activeBreakpoint} devices. Leave empty to inherit from desktop.
        </p>
      </div>

      {/* Common Responsive Overrides */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Typography</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Font Size
            </label>
            <input
              type="text"
              value={currentStyles.fontSize || ''}
              onChange={(e) => handleStyleChange('fontSize', e.target.value)}
              placeholder="Inherit from desktop"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Line Height
            </label>
            <input
              type="text"
              value={currentStyles.lineHeight || ''}
              onChange={(e) => handleStyleChange('lineHeight', e.target.value)}
              placeholder="Inherit from desktop"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Text Align
            </label>
            <select
              value={currentStyles.textAlign || ''}
              onChange={(e) => handleStyleChange('textAlign', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Inherit from desktop</option>
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
              <option value="justify">Justify</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Spacing</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Padding
            </label>
            <input
              type="text"
              value={currentStyles.padding || ''}
              onChange={(e) => handleStyleChange('padding', e.target.value)}
              placeholder="Inherit from desktop"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Margin
            </label>
            <input
              type="text"
              value={currentStyles.margin || ''}
              onChange={(e) => handleStyleChange('margin', e.target.value)}
              placeholder="Inherit from desktop"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Dimensions</h3>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Width
            </label>
            <input
              type="text"
              value={currentStyles.width || ''}
              onChange={(e) => handleStyleChange('width', e.target.value)}
              placeholder="Inherit from desktop"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Height
            </label>
            <input
              type="text"
              value={currentStyles.height || ''}
              onChange={(e) => handleStyleChange('height', e.target.value)}
              placeholder="Inherit from desktop"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Display
            </label>
            <select
              value={currentStyles.display || ''}
              onChange={(e) => handleStyleChange('display', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-[7px] focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Inherit from desktop</option>
              <option value="block">Block</option>
              <option value="inline">Inline</option>
              <option value="inline-block">Inline Block</option>
              <option value="flex">Flex</option>
              <option value="none">None (Hide)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveTab;

